import { FC, useMemo } from 'react';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import isEmpty from 'lodash-es/isEmpty';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { isBlueparrottFromContext, LanguageIsoType } from '@gn-audio/jss-components';
import { DEFAULT_CHINA_LOCALE, localeToBcp47 } from '@gn-audio/jss-core';

import { usePageProduct } from 'common/PageProductContext/pageProductContext';
import { isBlankPageTitle } from 'common/PageProductContext/PageTitleUtils';

import { RouteFields } from './LayoutTypes';

export interface PageMetaTagsProps {
  fields: RouteFields;
  currentLanguage: string;
  publicUrl: string;
  selectedLanguages?: LanguageIsoType[];
  selectedRegions?: LanguageIsoType[];
  filteredSelectedSites?: LanguageIsoType[];
}

type Regions = {
  [key: string]: string[];
};

const REGIONS: Regions = {
  'en-apac': [
    'en-apac',
    'en-bd',
    'en-kz',
    'en-bn',
    'en-la',
    'en-lk',
    'en-kh',
    'en-my',
    'en-tw',
    'en-mm',
    'en-th',
    'en-id',
    'en-pk',
    'en-vn',
    'en-ph',
    'vi-vn',
    'zh-tw',
  ],
  'en-emea': [
    'en-emea',
    'en-gr',
    'en-mt',
    'en-sk',
    'en-ma',
    'en-is',
    'en-ee',
    'en-lv',
    'en-pt',
    'en-ua',
    'en-lt',
    'en-lu',
    'el-gr',
    'hu-hu',
    'pt-pt',
  ],
  'en-mea': [
    'en-mea',
    'en-bh',
    'en-eg',
    'en-ir',
    'en-il',
    'en-ke',
    'en-lb',
    'en-sa',
    'en-ae',
    'en-zw',
    'en-ng',
    'en-na',
    'en-mz',
    'en-mu',
    'en-gh',
    'en-za',
  ],
  'nl-nl': ['nl-nl', 'nl-be'],
  'fr-fr': ['fr-fr', 'fr-be'],
  'de-de': ['de-de', 'de-at', 'de-be'],
  'fr-mea': ['fr-mea', 'fr-ma', 'fr-tn', 'fr-dz'],
  'es-lat': ['es-lat', 'es-co', 'es-ar', 'es-pe', 'es-pa', 'es-cl', 'es-cr'],
};
const ISO_ENGLISH = 'en';
const LINK_REL_ALTERNATE = 'alternate';
const JABRA_INT_DOMAIN = 'https://www.jabra.com';
const JABRA_CN_DOMAIN = 'https://www.jabra.cn';
const JABRA_FAVICON_PATH = '/favicons/jabra';
const BLUEPARROTT_FAVICON_PATH = '/favicons/blueparrott';
const JABRA_SITE_NAME = 'Jabra';
const BLUEPARROTT_SITE_NAME = 'BlueParrott';

const generateHrefLangLinks = (
  languages: LanguageIsoType[],
  currentPath: string,
  filteredSelectedSites: LanguageIsoType[],
  publicUrl: string,
  currentLanguage: string
) => {
  const regionalIsoCode: keyof LanguageIsoType = 'Regional Iso Code';
  let isEnLocalePresent = false;

  const createLink = (key: string, href: string, hrefLang: string) => {
    const isDefaultLocale = hrefLang === 'x-default';
    return (
      <link
        key={key}
        rel={LINK_REL_ALTERNATE}
        href={href}
        hrefLang={isDefaultLocale ? hrefLang : localeToBcp47(hrefLang)}
      />
    );
  };

  const getTargetLocale = (hrefLang: string): string => {
    for (const [regionKey, regionLanguages] of Object.entries(REGIONS)) {
      if (regionLanguages.includes(hrefLang)) {
        return regionKey;
      }
    }
    return hrefLang;
  };

  const createEnglishLinks = (language: LanguageIsoType, hrefLang: string) => {
    isEnLocalePresent = true;
    const url = getFullUrl(currentPath, publicUrl, currentLanguage);
    return [
      createLink(language.id, url, hrefLang),
      createLink(`${language.id}-x-default`, url, 'x-default'),
    ];
  };

  const createChinaLink = (language: LanguageIsoType, hrefLang: string) => {
    const url = getFullUrl(currentPath, JABRA_CN_DOMAIN);
    return createLink(language.id, url, hrefLang);
  };

  const createRegularLink = (language: LanguageIsoType, targetLocale: string, hrefLang: string) => {
    const url = getFullUrl(`/${targetLocale}${currentPath}`, publicUrl, currentLanguage);
    return createLink(language.id, url, hrefLang);
  };

  const filteredLanguages = filteredSelectedSites.length > 0 ? filteredSelectedSites : languages;

  const linkTags = filteredLanguages.flatMap((language) => {
    const languageIso = language?.Iso?.value?.toLowerCase();
    const hrefLang = language?.[regionalIsoCode]?.value?.toLowerCase();
    const targetLocale = getTargetLocale(hrefLang);

    if (languageIso === ISO_ENGLISH && !hrefLang) {
      return createEnglishLinks(language, languageIso);
    }

    if (hrefLang === DEFAULT_CHINA_LOCALE.toLowerCase()) {
      return createChinaLink(language, hrefLang);
    }

    return createRegularLink(language, targetLocale, hrefLang);
  });

  // Add x-default link if no English locale is present
  if (!isEnLocalePresent && filteredLanguages.length > 0) {
    const firstLanguage = filteredLanguages[0];
    const firstRegionalValue = firstLanguage?.[regionalIsoCode]?.value;

    if (firstRegionalValue) {
      const normalizedValue = firstRegionalValue.toLowerCase();
      const xDefaultUrl =
        normalizedValue === DEFAULT_CHINA_LOCALE.toLowerCase()
          ? getFullUrl(currentPath, JABRA_CN_DOMAIN)
          : getFullUrl(`/${normalizedValue}${currentPath}`, publicUrl, currentLanguage);

      linkTags.unshift(createLink(`${firstLanguage.id}-x-default`, xDefaultUrl, 'x-default'));
    }
  }

  return linkTags;
};

export const PageMetaTags: FC<PageMetaTagsProps> = ({
  fields,
  currentLanguage,
  selectedLanguages = [],
  selectedRegions = [],
  publicUrl,
  filteredSelectedSites = [],
}) => {
  const router = useRouter();
  const currentPath = router.asPath.split('?')[0].replace(/\/$/, '');
  const pageProduct = usePageProduct();

  const { sitecoreContext } = useSitecoreContext();
  const isBlueparrott = isBlueparrottFromContext(sitecoreContext);
  const faviconPath = isBlueparrott ? BLUEPARROTT_FAVICON_PATH : JABRA_FAVICON_PATH;
  const siteName = isBlueparrott ? BLUEPARROTT_SITE_NAME : JABRA_SITE_NAME;

  const pageFields = fields;
  const regionalIsoCode: keyof LanguageIsoType = 'Regional Iso Code';

  const hrefLinks = useMemo(
    () =>
      generateHrefLangLinks(
        selectedLanguages,
        currentPath,
        filteredSelectedSites,
        publicUrl,
        currentLanguage
      ),
    [selectedLanguages, currentPath, filteredSelectedSites, publicUrl, currentLanguage]
  );
  const { productName, product } = pageProduct;

  const ogImage = pageFields?.OpenGraphImage;
  const ogImagePath = getFullUrl(ogImage?.value.src ?? '', publicUrl);

  const pageTitle = pageFields?.PageTitle?.value;
  const pageDescription = pageFields?.PageDescription?.value;

  const canonicalUrlLocale = (() => {
    switch (currentLanguage.toLowerCase()) {
      case 'en':
        return getFullUrl(currentPath, publicUrl);
      case DEFAULT_CHINA_LOCALE.toLowerCase():
        return getFullUrl(currentPath, JABRA_CN_DOMAIN);
      default:
        return getFullUrl(`/${currentLanguage.toLowerCase()}${currentPath}`, `${publicUrl}`);
    }
  })();

  const pageTitleFinal = isBlankPageTitle(pageTitle) ? productName : pageTitle;

  const possibleDescriptions = [
    pageDescription,
    product?.summary,
    product?.description,
    product?.uniqueSellingPoint,
    pageTitleFinal,
  ];

  const pageDescriptionFinal = possibleDescriptions.find((description) => !isEmpty(description));

  let robotParts = [];
  if (pageFields?.NoIndex?.value) {
    robotParts.push('noindex');
  }
  if (pageFields?.NoFollow?.value) {
    robotParts.push('nofollow');
  }
  const robotText = robotParts.join(',');

  return (
    <Head>
      <title>{pageTitleFinal}</title>
      <meta name="description" content={pageDescriptionFinal} />
      <meta property="og:title" content={pageTitleFinal} />
      <meta property="og:description" content={pageDescriptionFinal} />
      {ogImage && (
        <>
          <meta property="og:image" content={ogImagePath} />
          <meta property="og:image:height" content={ogImage.value.height} />
          <meta property="og:image:width" content={ogImage.value.width} />
          <meta name="thumbnail" content={ogImagePath} />
        </>
      )}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteName} />
      <meta name="msapplication-TileColor" content="#ffffff" />
      <meta
        name="msapplication-TileImage"
        content={`${faviconPath}/web-app-manifest-192x192.png`}
      />
      {!isEmpty(robotText) && <meta name="robots" content={robotText} />}

      <link rel="canonical" href={canonicalUrlLocale} />
      <link rel="shortcut icon" href={`${faviconPath}/favicon.svg`} />
      <link rel="apple-touch-icon" href={`${faviconPath}/apple-touch-icon.png`} />
      <link rel="icon" type="image/svg+xml" href={`${faviconPath}/favicon.svg`} />
      <link rel="icon" type="image/png" sizes="96x96" href={`${faviconPath}/favicon-96x96.png`} />
      <link rel="icon" sizes="192x192" href={`${faviconPath}/web-app-manifest-192x192.png`} />
      {!!selectedRegions.length && (
        <>
          {selectedRegions.map((region) => {
            const regionalCode = region?.[regionalIsoCode]?.value?.toLowerCase();
            return (
              <meta
                key={`region-${regionalCode}`}
                name={`region-${regionalCode}`}
                content={getFullUrl(
                  `/${region?.[regionalIsoCode]?.value?.toLowerCase()}${currentPath}`,
                  `${publicUrl}`
                )}
              />
            );
          })}
        </>
      )}
      {!!hrefLinks.length && <>{hrefLinks.map((lang) => lang)}</>}
    </Head>
  );
};

const getFullUrl = (url: string, publicUrl: string, locale?: string) => {
  const normalizedUrl = url.startsWith('/') ? url : `/${url}`;

  try {
    if (locale?.toLocaleLowerCase() === DEFAULT_CHINA_LOCALE.toLocaleLowerCase()) {
      return new URL(normalizedUrl, JABRA_INT_DOMAIN).href;
    }

    return publicUrl ? new URL(normalizedUrl, publicUrl).href : '/';
  } catch (error) {
    console.error(
      `Failed to construct meta tag URL: url: ${normalizedUrl}, base: ${publicUrl}`,
      error
    );
    return '/';
  }
};
